import React, { type ReactElement } from 'react';
import { keyframes, styled } from '@compiled/react';
import Avatar from '@atlaskit/avatar';
import Button from '@atlaskit/button';
import DropdownMenu, {
	type CustomTriggerProps,
	DropdownItem,
	DropdownItemCheckbox,
} from '@atlaskit/dropdown-menu';
import SearchIcon from '@atlaskit/icon/glyph/search';
import Textfield from '@atlaskit/textfield';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import type { Priority, Page, PriorityOption } from '../types';
import messages from './messages';

export type PrioritiesPickerProps = {
	// this should be able to render anything
	renderTrigger: string | ((triggerButtonProps: CustomTriggerProps) => ReactElement);
	page: Page<PriorityOption>;
	isOpen: boolean;
	isLoading: boolean;
	filter: string;
	onSelect: (priority: PriorityOption) => void;
	onFilterChange: (value: string) => void;
	onClose: () => void;
	onLoadMore: () => void;
	onOpen: () => void;
	/**
	 * Whether to show a checkbox next to each item
	 */
	showCheckboxes: boolean;
	/**
	 * With showCheckboxes=true will be called to reset selected priorities
	 */
	onClearSelection?: () => void;
	/**
	 * Use null if you never want anything preselected
	 */
	selectedPriorityIds: ReadonlyArray<Priority['id']> | null;
	testId?: string;
};

const Skeleton = ({ total, loaded, page }: { total: number; loaded: number; page: number }) => {
	// if we don't have a total number yet, we simply use the page size
	// once the total number is available, we use it to calculate the number of skeletons to show on the last page
	const count = total ? Math.min(total - loaded, page) : page;
	return (
		<>
			{Array.from({ length: count }).map((_, index) => (
				<SkeletonRow key={index} height={16} width="100%" data-ignore="1" />
			))}
		</>
	);
};

const Filter = ({ onChange, value }: { onChange(value: string): void; value: string }) => {
	const { formatMessage } = useIntl();

	return (
		<FilterWrapper>
			<Textfield
				onChange={(e) => onChange(e.currentTarget.value)}
				elemAfterInput={
					<IconWrapper>
						<SearchIcon label={formatMessage(messages.searchFieldPlaceholder)} size="small" />
					</IconWrapper>
				}
				placeholder={formatMessage(messages.searchFieldPlaceholder)}
				value={value}
			/>
		</FilterWrapper>
	);
};

const Footer = ({
	loaded,
	isLoading,
	total,
	onLoadMore,
	onClearSelection,
}: {
	onLoadMore?: () => void;
	onClearSelection?: () => void;
	isLoading: boolean;
	loaded: number;
	total: number;
}) => {
	const { formatMessage } = useIntl();

	if (isLoading && !total) {
		// there is nothing useful to show in the footer while we are loading the first page
		return null;
	}

	let leftButton = null;
	if (onLoadMore) {
		leftButton =
			total > loaded ? (
				<Button spacing="compact" onClick={onLoadMore} isDisabled={isLoading}>
					{formatMessage(messages.loadMore)}
				</Button>
			) : null;
	} else if (onClearSelection) {
		leftButton = (
			<Button spacing="compact" onClick={onClearSelection}>
				{formatMessage(messages.clearSelection)}
			</Button>
		);
	}

	return (
		<FooterWrapper>
			{leftButton || <span />}
			{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
			<span>
				{formatMessage(messages.prioritiesCountInfo, {
					dispayedCount: loaded,
					totalCount: total,
				})}
			</span>
		</FooterWrapper>
	);
};

export default function PrioritiesPicker({
	renderTrigger,
	isOpen,
	isLoading,
	onClose,
	onOpen,
	onSelect,
	onClearSelection,
	onFilterChange,
	onLoadMore,
	filter,
	page: { items, pageSize, totalItems },
	showCheckboxes,
	selectedPriorityIds,
	testId,
}: PrioritiesPickerProps) {
	const { formatMessage } = useIntl();

	/*
	 * The DropdownItemCheckbox, as is today, does not allow both checkbox and elemBefore.
	 * That means we don't render priority icons next to the checkboxes unfortunately.
	 * We should explore if a custom component would do that.
	 */
	const Item = showCheckboxes ? DropdownItemCheckbox : DropdownItem;

	return (
		<DropdownMenu
			testId={testId}
			onOpenChange={isOpen ? onClose : onOpen}
			isOpen={isOpen}
			spacing="compact"
			trigger={renderTrigger}
		>
			<DialogContentWrapper>
				<Filter onChange={onFilterChange} value={filter} />
				<DropdownListWrapper>
					{items.map((item) => (
						<Item
							id={item.id}
							key={item.id}
							onClick={(e) => {
								e.stopPropagation();
								onSelect(item);
							}}
							isSelected={selectedPriorityIds?.includes(item.id)}
							elemBefore={
								<Avatar appearance="circle" label={item.name} size="xsmall" src={item.iconUrl} />
							}
						>
							{item.name}
						</Item>
					))}
					{!isLoading && !items.length && (
						<DropdownItem isDisabled>
							{formatMessage(filter ? messages.noMatchingPriorities : messages.noPriorities)}
						</DropdownItem>
					)}
					{showCheckboxes && !isLoading && totalItems > items.length ? (
						<Button appearance="link" onClick={onLoadMore}>
							{formatMessage(messages.loadMore)}
						</Button>
					) : null}
					{isLoading && <Skeleton total={totalItems} loaded={items.length} page={pageSize} />}
				</DropdownListWrapper>
				<Footer
					isLoading={isLoading}
					total={totalItems}
					loaded={items.length}
					onLoadMore={showCheckboxes ? undefined : onLoadMore}
					onClearSelection={showCheckboxes ? onClearSelection : undefined}
				/>
			</DialogContentWrapper>
		</DropdownMenu>
	);
}

const shimmer = keyframes({
	'0%': {
		backgroundPosition: '-100px 0',
	},
	'100%': {
		backgroundPosition: '300px 0',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DialogContentWrapper = styled.div({
	width: '300px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DropdownListWrapper = styled.div({
	width: '300px',
	overflowY: 'auto',
	maxHeight: '300px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FilterWrapper = styled.div({
	padding: `${token('space.100', '8px')} ${token('space.150', '12px')}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FooterWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	borderTop: `1px solid ${token('color.border', colors.N40)}`,
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
	flexDirection: 'row',
	padding: `${token('space.100', '8px')} ${token('space.150', '12px')}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconWrapper = styled.div({
	paddingRight: token('space.100', '8px'),
	display: 'flex',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SkeletonRow = styled.div<{ height: string | number; width: string | number }>({
	animationDuration: '1s',
	animationFillMode: 'forwards',
	animationIterationCount: 'infinite',
	animationName: shimmer,
	animationTimingFunction: 'linear',
	margin: `${token('space.100', '8px')} ${token('space.150', '12px')}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.background.neutral', colors.N30),
	backgroundImage: `linear-gradient(
        to right,
${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766 */ ''}
        ${token('color.background.neutral', colors.N30)} 10%,
${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766 */ ''}
        ${token('color.background.neutral.subtle', colors.N20)} 30%,
${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766 */ ''}
        ${token('color.background.neutral', colors.N30)} 50%
    )`,
	backgroundRepeat: 'no-repeat',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	height: (props) => `${props.height}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	width: (props) => `${props.width}px`,
	borderRadius: '3px',
});
